import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, AlertOctagon, Info, Zap, RefreshCw, Filter, X, Loader2, ExternalLink,
  ArrowLeft, CheckCircle2, Clock, TrendingDown, User, Building2, Globe,
} from "lucide-react";
import api from "@/lib/api";
import SystemHealthCard from "@/components/SystemHealthCard";

const LEVEL_META = {
  attention: { label: "Atenção",  color: "text-[hsl(var(--warning))]",  border: "border-l-amber-500",  icon: Info,          order: 1 },
  alert:     { label: "Alerta",   color: "text-[hsl(var(--warning))]", border: "border-l-orange-500", icon: AlertTriangle, order: 2 },
  critical:  { label: "Crítico",  color: "text-[hsl(var(--danger))]",    border: "border-l-red-500",    icon: AlertOctagon,  order: 3 },
  incident:  { label: "Incidente",color: "text-[hsl(var(--info))]", border: "border-l-purple-500", icon: Zap,           order: 4 },
};

const STATUS_META = {
  new:         { label: "Novo",           color: "bg-red-500" },
  analysing:   { label: "Em análise",     color: "bg-amber-500" },
  intervening: { label: "Em intervenção", color: "bg-blue-500" },
  resolved:    { label: "Resolvido",      color: "bg-emerald-500" },
  ignored:     { label: "Ignorado",       color: "bg-gray-400" },
};

const CATEGORY_LABEL = {
  sla_violated: "SLA violado",
  sla_critical: "SLA crítico",
  sla_escalation: "SLA escalonado",
  untouched_backlog: "Backlog não-tratado",
  low_conversion: "Conversão baixa",
  operator_low_score: "Score de operador",
  untouched_leads_period: "Leads por tratar",
  callback_unresponded: "Callback sem resposta",
  missed_clock_in: "Ponto em falta",
  forgotten_clock_out: "Ponto por bater",
};

const SCOPE_ICON = { global: Globe, office: Building2, operator: User, lead: ExternalLink };

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

// ---- Alert row ---------------------------------------------------------
function AlertRow({ alert, onUpdate }) {
  const meta = LEVEL_META[alert.level] || LEVEL_META.attention;
  const status = STATUS_META[alert.status] || STATUS_META.new;
  const Icon = meta.icon;
  const ScopeIcon = SCOPE_ICON[alert.scope_kind] || Globe;
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState(alert.action_taken || "");

  const patch = async (newStatus, actionText) => {
    setSaving(true);
    try {
      const body = { status: newStatus };
      if (actionText !== undefined) body.action_taken = actionText;
      const r = await api.patch(`/alerts/${alert.id}`, body);
      onUpdate(r.data);
      toast.success(`Marcado como ${STATUS_META[newStatus]?.label || newStatus}`);
    } catch (e) {
      toast.error("Erro ao atualizar");
    } finally { setSaving(false); }
  };

  return (
    <div className={`rounded-lg border border-border border-l-4 ${meta.border} bg-card shadow-sm hover:shadow-md hover:border-border transition-all p-4`} data-testid={`alert-row-${alert.id}`}>
      <div className="flex items-start gap-4">
        <div className={`w-9 h-9 rounded-md bg-muted/40 border border-border flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${meta.color}`} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold ${meta.color}`}>
              {meta.label}
            </span>
            <span className="text-[10px] text-muted-foreground/70">·</span>
            <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-medium">
              {CATEGORY_LABEL[alert.category] || alert.category}
            </span>
            {alert.scope_name && (
              <>
                <span className="text-[10px] text-muted-foreground">·</span>
                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                  <ScopeIcon className="w-3 h-3" />
                  {alert.scope_name}
                </span>
              </>
            )}
            <span className={`ml-auto inline-flex items-center gap-1 text-[10px] text-foreground font-semibold`}>
              <span className={`w-1.5 h-1.5 rounded-full ${status.color}`} />
              {status.label}
            </span>
          </div>
          <div className="font-semibold text-foreground text-sm">{alert.title}</div>
          <div className="text-sm text-[#4A4A4A] mt-0.5">{alert.message}</div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-muted-foreground">
            {alert.current_value !== null && alert.current_value !== undefined && (
              <span>Actual: <strong className="text-foreground">{alert.current_value}</strong></span>
            )}
            {alert.target_value !== null && alert.target_value !== undefined && (
              <span>Meta: <strong>{alert.target_value}</strong></span>
            )}
            {alert.deviation_pct !== null && alert.deviation_pct !== undefined && (
              <span className={alert.deviation_pct < 0 ? "text-[hsl(var(--danger))] font-semibold" : "text-[hsl(var(--success))] font-semibold"}>
                Desvio: {alert.deviation_pct > 0 ? "+" : ""}{alert.deviation_pct}%
              </span>
            )}
            {alert.affected_count > 0 && (
              <span>Afectados: <strong>{alert.affected_count}</strong></span>
            )}
            <span className="ml-auto">Criado {fmtDT(alert.created_at)}</span>
          </div>

          {alert.suggested_action && (
            <div className="mt-2 text-xs text-foreground bg-card/60 border border-border rounded-md px-2 py-1.5">
              <strong>Acção recomendada:</strong> {alert.suggested_action}
            </div>
          )}

          {alert.affected_leads?.length > 0 && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="mt-2 text-[11px] text-foreground hover:underline font-semibold"
              data-testid={`alert-toggle-${alert.id}`}
            >
              {expanded ? "Ocultar" : "Ver"} {alert.affected_leads.length} lead(s)
            </button>
          )}

          {expanded && alert.affected_leads?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {alert.affected_leads.slice(0, 30).map(lid => (
                <Link key={lid} to={`/leads/${lid}`}
                       className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-card border border-border hover:bg-muted/40"
                       data-testid={`alert-lead-${lid}`}>
                  {lid.slice(0, 8)}… <ExternalLink className="w-2.5 h-2.5" />
                </Link>
              ))}
            </div>
          )}

          {alert.status === "resolved" && alert.action_taken && (
            <div className="mt-2 text-xs text-[hsl(var(--success))] bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 rounded-md px-2 py-1.5">
              <strong>Acção tomada:</strong> {alert.action_taken}
              {alert.correction_time_minutes !== null && (
                <span className="ml-2 text-[10px] text-[hsl(var(--success))]">
                  ({alert.correction_time_minutes} min · resolvido por {alert.resolved_by})
                </span>
              )}
            </div>
          )}

          {["new", "analysing", "intervening"].includes(alert.status) && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/60 flex-wrap">
              {alert.status === "new" && (
                <button onClick={() => patch("analysing")}
                        disabled={saving}
                        className="text-xs px-3 py-1 rounded-md bg-card border border-border hover:bg-muted/40 font-semibold"
                        data-testid={`alert-analyse-${alert.id}`}>
                  Em análise
                </button>
              )}
              {["new", "analysing"].includes(alert.status) && (
                <button onClick={() => patch("intervening")}
                        disabled={saving}
                        className="text-xs px-3 py-1 rounded-md bg-blue-500 text-primary-foreground hover:bg-blue-600 font-semibold"
                        data-testid={`alert-intervene-${alert.id}`}>
                  Intervir
                </button>
              )}
              <input
                type="text"
                value={action}
                onChange={e => setAction(e.target.value)}
                placeholder="Descreve a acção tomada…"
                className="flex-1 min-w-[200px] px-2 py-1 rounded-md border border-border bg-card text-xs"
                data-testid={`alert-action-input-${alert.id}`}
              />
              <button onClick={() => patch("resolved", action)}
                      disabled={saving}
                      className="text-xs px-3 py-1 rounded-md bg-emerald-600 text-primary-foreground hover:bg-emerald-700 font-semibold"
                      data-testid={`alert-resolve-${alert.id}`}>
                Resolver
              </button>
              <button onClick={() => patch("ignored", action || "Ignorado sem justificação")}
                      disabled={saving}
                      className="text-xs px-3 py-1 rounded-md bg-card border border-border text-muted-foreground hover:bg-muted/40"
                      data-testid={`alert-ignore-${alert.id}`}>
                Ignorar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- PAGE --------------------------------------------------------------
export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState({ status: "active", level: "", category: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status && filter.status !== "active" && filter.status !== "all") params.status = filter.status;
      if (filter.level) params.level = filter.level;
      if (filter.category) params.category = filter.category;
      const [rList, rSum] = await Promise.all([
        api.get("/alerts", { params }),
        api.get("/alerts/summary"),
      ]);
      let alertsData = rList.data.alerts || [];
      if (filter.status === "active") alertsData = alertsData.filter(a => ["new","analysing","intervening"].includes(a.status));
      setAlerts(alertsData);
      setSummary(rSum.data);
    } catch (e) {
      toast.error("Erro a carregar alertas");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [JSON.stringify(filter)]);  // eslint-disable-line

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/alerts/scan");
      toast.success(`Scan completo: ${r.data.created} criados, ${r.data.updated} actualizados, ${r.data.auto_resolved} auto-resolvidos`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no scan");
    } finally { setScanning(false); }
  };

  const onAlertUpdate = (updated) => {
    setAlerts(list => list.map(a => a.id === updated.id ? updated : a));
    load();
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto space-y-6" data-testid="alerts-root">
      <div>
        <Link to="/performance" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2" data-testid="back-perf">
          <ArrowLeft className="w-3 h-3" /> Performance do Atendimento
        </Link>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Vigilância activa</div>
            <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Alertas de Performance</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Derrapagens detectadas em tempo real — SLA de 15 min, backlog, conversões abaixo da meta e score de operadores. Scan automático a cada 5 min.
            </p>
          </div>
          <button onClick={runScan} disabled={scanning}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 disabled:opacity-50"
                  data-testid="scan-btn">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Correr scan agora
          </button>
        </div>
      </div>

      {/* Cloudflare 520 sensor */}
      <SystemHealthCard />

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="alerts-summary">
          {Object.entries(LEVEL_META).map(([lvl, meta]) => {
            const count = summary.by_level[lvl] || 0;
            const Icon = meta.icon;
            return (
              <button key={lvl} onClick={() => setFilter(f => ({ ...f, level: f.level === lvl ? "" : lvl }))}
                      className={`text-left rounded-xl border-2 p-4 hover:shadow-md transition-all ${meta.bg} ${filter.level === lvl ? "ring-2 ring-[#0F172A]" : ""}`}
                      data-testid={`summary-${lvl}`}>
                <div className="flex items-center justify-between mb-2">
                  <Icon className={`w-4 h-4 ${meta.color}`} strokeWidth={2} />
                  <span className="text-lg">{meta.emoji}</span>
                </div>
                <div className={`text-[10px] uppercase tracking-[0.1em] font-bold ${meta.color}`}>{meta.label}</div>
                <div className="font-display text-3xl text-foreground tracking-tight mt-1">{count}</div>
              </button>
            );
          })}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-bold mb-1">
              <CheckCircle2 className="w-3 h-3" /> Resolvidos 30d
            </div>
            <div className="font-display text-3xl text-foreground">{summary.resolved_last_30d || 0}</div>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-bold mb-1">
              <Clock className="w-3 h-3" /> Tempo médio correcção
            </div>
            <div className="font-display text-3xl text-foreground">
              {summary.avg_correction_time_minutes != null ? `${summary.avg_correction_time_minutes}m` : "—"}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap bg-card border border-border rounded-xl p-3">
        <Filter className="w-3.5 h-3.5 text-muted-foreground" />
        <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
                className="px-3 py-1.5 rounded-lg border border-border text-sm bg-card"
                data-testid="filter-status">
          <option value="active">Só activos</option>
          <option value="all">Todos</option>
          <option value="new">Novos</option>
          <option value="analysing">Em análise</option>
          <option value="intervening">Em intervenção</option>
          <option value="resolved">Resolvidos</option>
          <option value="ignored">Ignorados</option>
        </select>
        <select value={filter.category} onChange={e => setFilter(f => ({ ...f, category: e.target.value }))}
                className="px-3 py-1.5 rounded-lg border border-border text-sm bg-card"
                data-testid="filter-category">
          <option value="">Todas as categorias</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {(filter.level || filter.category || (filter.status !== "active")) && (
          <button onClick={() => setFilter({ status: "active", level: "", category: "" })}
                  className="text-xs text-[hsl(var(--danger))] hover:underline font-semibold flex items-center gap-1"
                  data-testid="filter-clear">
            <X className="w-3 h-3" /> Limpar
          </button>
        )}
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> A carregar alertas…
        </div>
      )}

      {!loading && alerts.length === 0 && (
        <div className="border-2 border-dashed border-[hsl(var(--success))]/40 rounded-2xl p-12 text-center bg-[hsl(var(--success-bg))]/30" data-testid="alerts-empty">
          <CheckCircle2 className="w-12 h-12 mx-auto text-[hsl(var(--success))] mb-3" />
          <div className="font-display text-2xl text-[hsl(var(--success))] mb-1">Zero alertas activos</div>
          <p className="text-sm text-[hsl(var(--success))]">Nenhuma derrapagem detectada no âmbito seleccionado. O último scan foi há &lt; 5 minutos.</p>
        </div>
      )}

      {!loading && alerts.length > 0 && (
        <div className="space-y-3" data-testid="alerts-list">
          {alerts.map(a => (
            <AlertRow key={a.id} alert={a} onUpdate={onAlertUpdate} />
          ))}
        </div>
      )}
    </div>
  );
}
